<template>
  <div class="app-wrapper" :class="[isLoading ? 'is-loading' : null, 'app-locale-' + locale]">
    <template v-if="!isLoading">
      <step-0 v-if="currentStep === 0" />
      <step-1 v-if="currentStep === 1" />
      <step-2 v-if="currentStep === 2" />
      <step-3-terrace v-if="currentStep === 3 && datas.step0 && datas.step0.type === 'terrace'" />
      <step-3-shade v-if="currentStep === 3 && datas.step0 && datas.step0.type === 'shade'" />
      <step-4-terrace v-if="currentStep === 4 && datas.step0 && datas.step0.type === 'terrace'"/>
      <step-4-shade v-if="currentStep === 4 && datas.step0 && datas.step0.type === 'shade'"/>
      <step-5 v-if="currentStep === 5" :typeStep="datas.step0.type"/>
      <final v-if="currentStep === 6" />
      <confirmation v-if="currentStep === 7" />
      <demand v-if="currentStep === 'finished'" />
    </template>

    <salesforce-popin v-if="popinSalesforceIsOpen" />
    <save-popin v-if="popinSaveIsOpen" />
    <help-popin v-if="popinHelpIsOpen" :image="popinHelpImage" />
  </div>
</template>
<style lang="scss">
@import "~@/soprasolar-project/assets/styles/import";
.viewer-element {
  .app-wrapper {
    position: relative;
    @include wrapper;
    @include vertical-spacing-bottom;
    padding-bottom: rem(1);
    .std-form {
      margin-bottom: 0;
    }
    &.is-loading {
      min-height: rem(500);
      @include loader($color-main, 1.5);
    }
    &.app-locale-en {
      .radio-tile--shade {
        display: none;
      }
    }
  }
  .help-text {
    font-size: rem(12);
    color: $grey;
    font-style: italic;
  }
  .project-wrapper {
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    @include desktop {
      width: percentage(10/12);
    }
  }
  .project-container {
    @include vertical-spacing-bottom;
    @include tablet {
      display: flex;
    }
  }
  .project-content {
    @include mobile {
      margin-bottom: rem(40);
    }
    @include tablet {
      width: 50%;
      flex: 1 0 0;
      &:not(:first-child) {
        border-left: rem(1) dashed $black;
        padding-left: rem(20);
      }
      &:not(:last-child) {
        padding-right: rem(20);
      }
    }
    @include desktop {
      &:not(:first-child) {
        padding-left: rem(40);
      }
      &:not(:last-child) {
        padding-right: rem(40);
      }
    }
  }
  .project-current-step {
    color: $color-second;
  }
  .project-title {
    @include title(2);
  }
  .project-subtitle {
    @include title(3);
    font-weight: normal;
    color: $color-second;
  }
  .project-footer {
    text-align: center;
    margin-bottom: rem(40);
  }
  .project-actions {
    display: flex;
    flex-direction: row-reverse;
    gap: rem(60);
    align-content: center;
    justify-content: center;
  }
  .button-link {
    @include link;
  }
  .button-flat {
    @include cta;
    height: rem(50);
  }
  .project-legend {
    color: $color-main;
    text-align: right;
    font-size: rem(14);
  }
  .std-form {
    .form-error {
      opacity: 1;
    }
  }
  .radios {
    display: flex;
    flex-wrap: wrap;
    gap: rem(20);
    .radio {
      width: calc(50% - #{rem(10)});
      .radio-input {
        @include hideAccess;
        &:checked {
          & + .radio-label::after {
            background-color: $color-second;
          }
        }
        &:focus {
          & + .radio-label {
            box-shadow: 0 0 0 rem(3) rgba($color-second, .5);
            border-color: rgba($color-second, .5);
          }
        }
      }
      .radio-label {
        cursor: pointer;
        position: relative;
        width: 100%;
        border: rem(1) solid $grey-light;
        padding: rem(10) rem(20);
        padding-left: rem(50);
        border-radius: rem(8);
        transition: border-color $transition-out-hover;
        &:hover, &:focus {
          border-color: $color-second;
          transition: border-color $transition-in-hover;
        }
        &::after,
        &::before {
          content: '';
          display: block;
          border-radius: 100%;
          border: rem(1) solid $color-second;
          width: rem(18);
          height: rem(18);
          position: absolute;
          top: rem(23);
          transform: translateY(-50%);
          left: rem(20);
        }
      }
      .radio-label-text {
        font-weight: bold;
        min-height: rem(30);
        line-height: 1.1;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        span {
          font-weight: normal;
          font-size: rem(12);
          color: $grey;
          &::before {
            content: '(';
          }
          &::after {
            content: ')';
          }
        }
      }
      .radio-label-media {
        position: relative;
        margin-left: rem(-30);
        width: calc(100% + #{rem(30)});
        padding-top: 75%;
        overflow: hidden;
        margin-top: rem(5);
        img {
          position: absolute;
          top: 0;
          left: 0;
          object-fit: contain;
          height: 100%;
          width: 100%;
        }
      }
    }
  }
  select > option[value=""] {
    display: none;
  }
}
</style>
<script>
import { gsap } from 'gsap/all';

import AppControlService from '@/soprasolar-project/services/AppControl';
import Step0 from '@/soprasolar-project/pages/Step0';
import Step1 from '@/soprasolar-project/pages/Step1';
import Step2 from '@/soprasolar-project/pages/Step2';
import Step3Terrace from '@/soprasolar-project/pages/Step3-terrace';
import Step3Shade from '@/soprasolar-project/pages/Step3-shade';
import Step4Terrace from '@/soprasolar-project/pages/Step4-terrace';
import Step4Shade from '@/soprasolar-project/pages/Step4-shade';
import Step5 from '@/soprasolar-project/pages/Step5';
import Final from '@/soprasolar-project/pages/Final';
import Confirmation from '@/soprasolar-project/pages/Confirmation';
import Demand from '@/soprasolar-project/pages/Demand';
import SavePopin from '@/soprasolar-project/components/Save-popin';
import SalesforcePopin from '@/soprasolar-project/components/Salesforce-popin';
import HelpPopin from '@/soprasolar-project/components/Help-popin';

export default {
  name: 'App',
  components: {
    Step0,
    Step1,
    Step2,
    Step3Terrace,
    Step3Shade,
    Step4Terrace,
    Step4Shade,
    Step5,
    Final,
    Confirmation,
    Demand,
    SavePopin,
    SalesforcePopin,
    HelpPopin
  },
  data: () => {
    return {
      isLoading: true,
      locale: window.Translator.locale,
      popinSaveIsOpen: false,
      popinSalesforceIsOpen: false,
      popinHelpIsOpen: false,
      popinHelpImage: null,
      datas: AppControlService.datas,
      currentStep: AppControlService.currentStep,
    }
  },
  created () {
    AppControlService.on('step', (step) => {
      this.currentStep = step;
      gsap.to(window, { duration: .6, scrollTo: 0 });
    });
    AppControlService.on('datas', (datas) => {
      this.datas = datas;
    });
    AppControlService.on('popinSave', (isOpen) => {
      this.popinSaveIsOpen = isOpen;
    });
    AppControlService.on('popinSalesForce', (isOpen) => {
      this.popinSalesforceIsOpen = isOpen;
    });
    AppControlService.on('popinHelp', (isOpen, image) => {
      this.popinHelpIsOpen = isOpen;
      this.popinHelpImage = image;
    });
  },
  mounted () {
    const uri = window.location.href;
    const url = new URL(uri);
    const token = url.searchParams.get('token');
    if( token ) {
      AppControlService.getDatas(token).then().catch(console.error).then(() => {
        this.isLoading = false;
      })
    } else this.isLoading = false;
  },
  methods: { }
};
</script>
